import React from "react";
import "./App.scss";
import * as Sentry from "@sentry/react";

Sentry.init({
  dsn: process.env.SENTRY_DSN,
  environment: process.env.NODE_ENV,
  tracesSampleRate: 0.2,
  enabled: process.env.NODE_ENV === "production",
});

import { BrowserRouter, Switch, Route, Redirect } from "react-router-dom";
import { ActionCableProvider } from "frontend/utils/actionCableProvider";

import { GamesLayout, GamesRoutes } from "frontend/routes/Games";
import Landing from "frontend/routes/Landing";
import UserProfile from "frontend/routes/UserProfile";
import Admin from "frontend/routes/Admin/Admin";
import NavBar from "frontend/components/NavBar";
import LoginModal from "frontend/components/LoginModal";
import SignupModal from "frontend/components/SignupModal";
import ConfirmationModal from "frontend/components/ConfirmationModal";
import ForgotPasswordModal from "frontend/components/ForgotPasswordModal";
import ResetPasswordModal from "frontend/components/ResetPasswordModal";
import { getAuthState, clearSession, getAccessToken } from "frontend/utils/authenticateHelper.js";
import { config } from "frontend/utils/constants.js";
import Cookies from "js-cookie";

class App extends React.Component {
  constructor(props) {
    super(props);
    const urlParams = new URLSearchParams(window.location.search);
    const resetToken = urlParams.get('reset_password_token');

    this.state = {
      authState: getAuthState(),
      showLoginModal: false,
      showSignupModal: false,
      showLogoutModal: false,
      showForgotPasswordModal: false,
      showResetPasswordModal: !!resetToken,
      resetPasswordToken: resetToken,
      sidebarOpen: false,
      hasPendingChallenges: false,
      cableUrl: `${config.url.API_WS_ROOT}?access_token=${getAccessToken()}`
    };
    this.refreshAuthState = this.refreshAuthState.bind(this);
    this.handleLogout = this.handleLogout.bind(this);
    this.openLoginModal = this.openLoginModal.bind(this);
    this.openSignupModal = this.openSignupModal.bind(this);
    this.openLogoutModal = this.openLogoutModal.bind(this);
    this.openForgotPasswordModal = this.openForgotPasswordModal.bind(this);
    this.closeModals = this.closeModals.bind(this);
    this.switchToSignup = this.switchToSignup.bind(this);
    this.switchToLogin = this.switchToLogin.bind(this);
    this.handlePasswordResetSuccess = this.handlePasswordResetSuccess.bind(this);
    this.toggleSidebar = this.toggleSidebar.bind(this);
    this.closeSidebar = this.closeSidebar.bind(this);
    this.handleChallengesChange = this.handleChallengesChange.bind(this);
  }

  refreshAuthState() {
    this.setState({
      authState: getAuthState(),
      cableUrl: `${config.url.API_WS_ROOT}?access_token=${getAccessToken()}`
    });
  }

  handleLogout() {
    clearSession();
    this.setState({
      authState: 'anonymous',
      showLogoutModal: false,
      hasPendingChallenges: false,
      cableUrl: null
    });
  }

  openLogoutModal() {
    this.setState({ showLogoutModal: true });
  }

  openLoginModal() {
    this.setState({ showLoginModal: true, showSignupModal: false });
  }

  openSignupModal() {
    this.setState({ showSignupModal: true, showLoginModal: false });
  }

  openForgotPasswordModal() {
    this.setState({ showForgotPasswordModal: true, showLoginModal: false });
  }

  closeModals() {
    this.setState({
      showLoginModal: false,
      showSignupModal: false,
      showLogoutModal: false,
      showForgotPasswordModal: false,
      showResetPasswordModal: false,
      resetPasswordToken: null
    });
    if (window.location.search.includes('reset_password_token')) {
      window.history.replaceState({}, '', window.location.pathname);
    }
  }

  handlePasswordResetSuccess() {
    this.closeModals();
    this.openLoginModal();
  }

  switchToSignup() {
    this.setState({ showLoginModal: false, showSignupModal: true });
  }

  switchToLogin() {
    this.setState({ showSignupModal: false, showForgotPasswordModal: false, showLoginModal: true });
  }

  toggleSidebar() {
    this.setState(prev => ({ sidebarOpen: !prev.sidebarOpen }));
  }

  closeSidebar() {
    this.setState({ sidebarOpen: false });
  }

  handleChallengesChange(hasPending) {
    this.setState({ hasPendingChallenges: hasPending });
  }

  render() {
    const username = Cookies.get("username");
    // Guests are authenticated, so anything that only needs API access uses
    // this boolean; only the pieces that treat guests differently read authState.
    const isLoggedIn = this.state.authState !== 'anonymous';

    return (
      <BrowserRouter>
        <div className="App">
          <ActionCableProvider url={this.state.cableUrl}>
            <NavBar
              authState={this.state.authState}
              username={username}
              onLoginClick={this.openLoginModal}
              onSignupClick={this.openSignupModal}
              onLogoutClick={this.openLogoutModal}
              onMenuClick={this.toggleSidebar}
              menuOpen={this.state.sidebarOpen}
              hasPendingChallenges={this.state.hasPendingChallenges}
            />
            {/* Remount on auth change so a claimed guest immediately sees their
                new username instead of game data fetched as guest-xxxxxx. */}
            <GamesLayout
              key={this.state.authState}
              isAuthenticated={isLoggedIn}
              isGuest={this.state.authState === 'guest'}
              onSignupClick={this.openSignupModal}
              sidebarOpen={this.state.sidebarOpen}
              onCloseSidebar={this.closeSidebar}
              onChallengesChange={this.handleChallengesChange}
            >
              <Switch>
                <Route
                  exact
                  path="/"
                  render={() => {
                    if (this.state.showResetPasswordModal) {
                      return null;
                    }
                    if (isLoggedIn) {
                      return <Redirect to="/games" />;
                    }
                    return (
                      <Landing onGuestStart={this.refreshAuthState} />
                    );
                  }}
                />
                <Route
                  path="/games"
                  render={(props) => (
                    <GamesRoutes {...props} onSignupClick={this.openSignupModal} />
                  )}
                />
                <Route path="/users/:username" component={UserProfile} />
                <Route exact path="/admin" component={Admin} />
              </Switch>
            </GamesLayout>

            {this.state.showLoginModal && (
              <LoginModal
                onClose={this.closeModals}
                onLogin={this.refreshAuthState}
                onSwitchToSignup={this.switchToSignup}
                onForgotPassword={this.openForgotPasswordModal}
              />
            )}

            {this.state.showForgotPasswordModal && (
              <ForgotPasswordModal
                onClose={this.closeModals}
                onBackToLogin={this.switchToLogin}
              />
            )}

            {this.state.showResetPasswordModal && (
              <ResetPasswordModal
                resetToken={this.state.resetPasswordToken}
                onClose={this.closeModals}
                onSuccess={this.handlePasswordResetSuccess}
              />
            )}

            {this.state.showSignupModal && (
              <SignupModal
                isGuest={this.state.authState === 'guest'}
                onClose={this.closeModals}
                onLogin={this.refreshAuthState}
                onSwitchToLogin={this.switchToLogin}
              />
            )}

            {this.state.showLogoutModal && (
              <ConfirmationModal
                title="Logout"
                message="Are you sure you want to logout?"
                confirmText="Logout"
                cancelText="Cancel"
                onConfirm={this.handleLogout}
                onCancel={this.closeModals}
              />
            )}
          </ActionCableProvider>
        </div>
      </BrowserRouter>
    );
  }
}

export default App;
